import React, { useEffect, useMemo, useRef } from 'react'
import { ApplicationFiltersNew } from 'types/campaign/applications'
import AppliedFilterBadge from './AppliedFilterBadge'
import './style.scss'

interface AppliedFiltersProps {
    filters: ApplicationFiltersNew
    onChange: (updatedFilters: ApplicationFiltersNew) => void
}

export const SKIP_FILTER_KEYS = new Set([
    'page',
    'sort',
    'q',
    'audience_gender_threshold',
    'audience_cities_threshold',
    'audience_age_groups_threshold',
    'declined',
    'bookmarked',
])

const AppliedFilters: React.FC<AppliedFiltersProps> = ({ filters, onChange }) => {
    const scrollRef = useRef<HTMLDivElement>(null)

    const displayFilters = useMemo(() => {
        const result: Array<{
            id: string;
            key: string;
            sourceKey: string;
            onRemove: () => void;
            value: string | number;
        }> = []

        if (filters.age_min && filters.age_max) {
            result.push({
                id: 'age_range',
                key: 'age_range',
                sourceKey: 'age_range',
                onRemove: () => {
                    const updatedFilters = { ...filters }
                    delete updatedFilters.age_min
                    delete updatedFilters.age_max
                    onChange(updatedFilters)
                },
                value: `${filters.age_min} - ${filters.age_max}`,
            })
        } else {
            if (filters.age_min) {
                result.push({
                    id: 'age_min',
                    key: 'age_min',
                    sourceKey: 'age_min',
                    onRemove: () => {
                        const updatedFilters = { ...filters }
                        delete updatedFilters.age_min
                        onChange(updatedFilters)
                    },
                    value: filters.age_min,
                })
            } else if (filters.age_max) {
                result.push({
                    id: 'age_max',
                    key: 'age_max',
                    sourceKey: 'age_max',
                    onRemove: () => {
                        const updatedFilters = { ...filters }
                        delete updatedFilters.age_max
                        onChange(updatedFilters)
                    },
                    value: filters.age_max,
                })
            }
        }
        for (const [key, value] of Object.entries(filters)) {
            if (SKIP_FILTER_KEYS.has(key)) continue;
            if (key === 'age_min' || key === 'age_max') continue;
            if (Array.isArray(value)) {
                for (const item of value) {
                    result.push({
                        id: `${key}:${item}`,
                        key,
                        sourceKey: key,
                        onRemove: () => {
                            onChange({ ...filters, [key]: value.filter((removedItem: string) => removedItem !== item) })
                        },
                        value: item,
                    })
                }
            } else if (value !== undefined && value !== null && value !== '') {
                result.push({
                    id: key,
                    key,
                    sourceKey: key,
                    onRemove: () => {
                        const updatedFilters = { ...filters }
                        delete updatedFilters[key]
                        onChange(updatedFilters)
                    },
                    value: value as string | number,
                })
            }
        }

        return result;
    }, [filters, onChange])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        const onWheel = (event: WheelEvent) => {
            if (el.scrollWidth <= el.clientWidth) return

            const delta =
                Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
            if (delta === 0) return

            event.preventDefault()
            el.scrollLeft += delta
        }

        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [displayFilters.length])

    const scrollClassName = 'applied-filters-toolbar__scroll'

    if(displayFilters.length === 0) return (
        <div ref={scrollRef} className={scrollClassName}> 
            <span className="applied-filters-toolbar__text">No filters applied</span>
        </div>
    );

    return (
        <div ref={scrollRef} className={scrollClassName}>
            {displayFilters.map((displayFilter) => (
                <AppliedFilterBadge
                    key={displayFilter.id}
                    filterKey={displayFilter.key}
                    value={displayFilter.value}
                    onRemove={displayFilter.onRemove}
                />
            ))}
        </div>
    )
}

export default AppliedFilters
